import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute } from '@angular/router';
import {
  ProductResponse,
  ZentraComponent,
  ZentraConnectSiteStatus,
} from '../../../interfaces/subProduct.interface';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { SubComponentsService } from '../../../Services/sub-components.service';
import { SubProductsService } from '../../../Services/sub-products.service';
import { AddressServiceService } from '../../../Services/address-service.service';
import { ChargifyFormComponent } from '../../chargify-form/chargify-form.component';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PurchaseConfirmationDialogComponent } from '../../PropertyManager/purchase-confirmation-dialog/purchase-confirmation-dialog.component';
import { calculateComponentsTotal } from '../../../Shared Components/Utilities/utils';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MaxioCustomerService } from '../../../Services/maxio-customer.service';
import { PreviewProformaInvoiceService } from '../../../Services/preview-proforma-invoice.service';
import { AuthService } from '../../../Services/Auth/auth.service';
import { ConnectComponentService } from '../../../Services/connect-component.service';
import { MaxioCustomer } from '../../../interfaces/maxioCustomerInfo';
@Component({
  selector: 'app-integrator-stepper',
  templateUrl: './integrator-stepper.component.html',
  styleUrls: ['./integrator-stepper.component.scss'],

  standalone: false,
})
/* istanbul ignore next */
export class IntegratorStepperComponent implements AfterViewInit {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('chargifyForm') chargifyFormComponent!: ChargifyFormComponent;
  isLinear = true;
  firstFormGroup!: FormGroup;
  secondFormGroup!: FormGroup;
  products: ProductResponse[] = [];
  selectedProduct: ProductResponse | null = null;
  componentOptions: ZentraComponent[] = [];
  selectedComponents: { component: ZentraComponent; quantity: number }[] = [];
  totalPrice: number = 0;
  selectedComponent: ZentraComponent | null = null;
  quantity: number = 1;
  isSaveButtonDisabled: boolean = true;
  isEditing: boolean = false;
  originalFormValues: any = null;
  isDataReady: boolean = false;
  customerInfoGroupInt!: FormGroup;
  validAddressSelected: boolean = false;
  selectedAddress: string = '';
  addressSuggestions: any[] = [];
  usStates: { name: string; abbreviation: string }[] = [];
  billingDetailsGroupInt!: FormGroup;
  usePropertyDetails: boolean = false;
  chargifyFormValid: boolean = false;
  paymentGroup!: FormGroup;
  chargifyToken: string = '';
  maxioErrors: string[] = [];
  billingDate: string | null = null;
  chargifyDetails = {
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  };
  termsAccepted: boolean = false;
  achTermsAccepted: boolean = false;
  isAch: boolean = false;
  subscriptions: Subscription = new Subscription();
  achAgreement: any = null;
  previewData: any = null;
  lineItems: any[] = [];
  subtotal: number = 0;
  totalTax: number = 0;
  total: number = 0;
  propertyAddressUnknown = false;
  allComponentOptions: ZentraComponent[] = [];
  invoicePreviewUrl: string | null = null;
  loadingInvoicePreview = false;
  invoicePreviewError: string | null = null;
  wholesalers: any[] = [];
  wholesalerGroup!: FormGroup;
  userRole: string = '';
  existingCustomerData: MaxioCustomer | null = null;
  hasExistingCustomer: boolean = false;
  isExistingZentraConnectSite: ZentraConnectSiteStatus = null;
  mandatoryComponents: ZentraComponent[] = [];
  mandatoryQtyEditableIds: Set<number> = new Set();

  constructor(
    readonly formBuilder: FormBuilder,
    readonly productService: SubProductsService,
    readonly subComponentsService: SubComponentsService,
    readonly loadingService: LoadingService,
    readonly cdr: ChangeDetectorRef,
    readonly backend: BackendAccessService,
    readonly dialog: MatDialog,
    readonly route: ActivatedRoute,
    readonly renderer: Renderer2,
    readonly addressService: AddressServiceService,
    readonly maxioCustomerService: MaxioCustomerService,
    readonly previewInvoiceSvc: PreviewProformaInvoiceService,
    private readonly authService: AuthService,
    readonly connectComponentService: ConnectComponentService
  ) {}
  /* istanbul ignore next */
  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  /* istanbul ignore next */
  async ngOnInit() {
    this.initializeForms();
    const permissions = await firstValueFrom(
      this.authService
        .getPermissions()
        .pipe(filter((perms) => perms.length > 0))
    );
    if (permissions.includes('Subscription View For GuestPurchaser')) {
      this.userRole = 'GuestPurchaser';
    } else {
      this.userRole = 'Integrator';
    }

    // Subscribe to customer data changes
    this.subscriptions.add(
      this.maxioCustomerService.customerData$.subscribe((customerData) => {
        this.existingCustomerData = customerData;
        this.hasExistingCustomer = customerData !== null;

        // If we have existing customer data, pre-populate and disable the form
        if (this.hasExistingCustomer && this.existingCustomerData) {
          this.customerInfoGroupInt.patchValue({
            integrator_f_name: this.existingCustomerData.first_name,
            integrator_l_name: this.existingCustomerData.last_name,
            integrator_company: this.existingCustomerData.organization ?? '',
            integrator_email: this.existingCustomerData.email,
            integrator_phone: this.existingCustomerData.phone ?? '',
            integrator_address: this.existingCustomerData.address,
            integrator_city: this.existingCustomerData.city,
            integrator_state: this.existingCustomerData.state,
            integrator_zip: this.existingCustomerData.zip,
            integrator_country: this.existingCustomerData.country,
          });
          // Disable the form since customer already exists
          this.customerInfoGroupInt.disable();
        } else {
          // Enable the form if no customer data
          this.customerInfoGroupInt.enable();
        }
        this.cdr.detectChanges();
      })
    );

    // Show loading indicator
    Promise.all([this.fetchZentraProducts(), this.fetchComponentOptions()])
      .then(() => {
        this.isDataReady = true;
        this.firstFormGroup.valueChanges.subscribe(() =>
          this.checkForChanges()
        );
        this.secondFormGroup.valueChanges.subscribe(() =>
          this.checkForChanges()
        );
      })
      .catch((error) => {
        console.error('Error during initialization:', error);
      })
      .finally(() => {
        this.loadingService.hide();
      });
    this.components.valueChanges.subscribe(() => {
      this.updateSelectedComponentsInt();
    });
    this.usStates = this.addressService.getUSStates();

    this.wholesalers = this.authService.getWholesalers();

    // Logic: If only one wholesaler, auto-select it
    if (this.wholesalers.length === 1) {
      this.wholesalerGroup.patchValue({
        wholesalerUserEmail: this.wholesalers[0].userEmail,
      });
    }
    this.cdr.detectChanges();
  }

  /* istanbul ignore next */
  private async getRoleType(): Promise<
    'Integrator' | 'GuestPurchaser' | 'Standard'
  > {
    const permissions = await firstValueFrom(
      this.authService
        .getPermissions()
        .pipe(filter((perms) => perms.length > 0))
    );

    if (permissions.includes('Subscription View For Integrator')) {
      return 'Integrator';
    }
    if (permissions.includes('Subscription View For GuestPurchaser')) {
      return 'GuestPurchaser';
    }
    return 'Standard';
  }

  /* istanbul ignore next */
  checkForChanges() {
    if (!this.isEditing) {
      this.isSaveButtonDisabled = false; // Enable Save for new configurations
      return;
    }
    // Compare current values with original values
    const currentValues = {
      firstForm: this.firstFormGroup.value,
      secondForm: this.secondFormGroup.value,
    };
    this.isSaveButtonDisabled =
      JSON.stringify(currentValues) === JSON.stringify(this.originalFormValues);
  }
  /* istanbul ignore next */
  markAllControlsTouched(): void {
    this.firstFormGroup.markAllAsTouched();
    this.secondFormGroup.markAllAsTouched();
    this.metafieldsGroup.markAllAsTouched();
  }

  /* istanbul ignore next */
  async fetchZentraProducts(): Promise<void> {
    this.loadingService.show();
    const role = await this.getRoleType();

    let obs$;
    if (role === 'Integrator') {
      obs$ = this.productService.getZentraProductsIntegrator();
    } else if (role === 'GuestPurchaser') {
      obs$ = this.productService.getZentraProductsGuestPurchaser();
    } else {
      obs$ = this.productService.getZentraProducts();
    }

    return new Promise((resolve, reject) => {
      obs$.subscribe({
        next: (products) => {
          this.products = products;
          resolve();
          this.loadingService.hide();
        },
        error: (err) => {
          this.loadingService.hide();
          reject(new Error(err?.message || 'Error fetching products'));
        },
      });
    });
  }

  /* istanbul ignore next */
  async fetchComponentOptions(): Promise<void> {
    const role = await this.getRoleType();

    // 1. Trigger the fetch in the SubComponentsService based on role
    if (role === 'Integrator') {
      await this.subComponentsService.fetchComponentOptionsIntegrator();
    } else if (role === 'GuestPurchaser') {
      // Ensure this method exists in your subComponentsService
      await this.subComponentsService.fetchComponentOptionsGuestPurchaser();
    } else {
      await this.subComponentsService.fetchComponentOptions();
    }

    // 2. Get the results from the service's observable.
    // Only allComponentOptions is managed here; componentOptions is always set by applyComponentMapping.
    return new Promise((resolve, reject) => {
      this.subComponentsService.getComponentOptions().subscribe({
        next: (components) => {
          const hadData = this.allComponentOptions.length > 0;
          this.allComponentOptions = components;
          if (!hadData && components.length > 0 && this.selectedProduct) {
            // Components arrived after product was already selected — re-apply mapping
            this.applyComponentMapping(this.selectedProduct.product.id);
            this.cdr.markForCheck();
          } else if (!this.selectedProduct) {
            // No product selected yet — safe to show all as default
            this.componentOptions = components;
          }
          resolve();
        },
        error: (err) => {
          reject(new Error(err?.message || 'Error fetching component options'));
        },
      });
    });
  }

  /* istanbul ignore next */
  public initializeForms(): void {
    this.firstFormGroup = this.formBuilder.group({
      maxioProductId: ['', Validators.required],
      product_id: ['', Validators.required],
      product_name: [''],
      isExistingZentraConnectSite: [null],
      poNumber: [''],
    });
    this.secondFormGroup = this.formBuilder.group({
      components: this.formBuilder.array([this.createComponentControl()]),
      metafields: this.formBuilder.group({
        projectType: ['', Validators.required],
        implementationStartDate: [
          '',
          [Validators.required, todayOrFutureDateValidator()],
        ],
        expectedOccupancyDate: [
          '',
          [Validators.required, todayOrFutureDateValidator()],
        ],
        engageMigration: ['', Validators.required],
        engageSiteName: [''],
        engageSiteId: [''],
        expectedMigrationDate: ['', todayOrFutureDateValidator()],
        propertyWebsite: [''],
        property_name: ['', Validators.required],
        property_address: ['', Validators.required],
        property_city: ['', [Validators.required, alphabeticValidatorInt()]],
        property_state: ['', Validators.required],
        property_zip: ['', [Validators.required, zipCodeValidatorInt()]],
        property_country: [{ value: 'US', disabled: true }],
        property_manager_f_name: ['', Validators.required],
        property_manager_l_name: ['', Validators.required],
        property_manager_email: [
          '',
          [Validators.required, strictEmailValidatorIntegrator],
        ],
        property_manager_phone: ['', [cleanPhoneNumberValidatorInt()]],
      }),
    });
    this.customerInfoGroupInt = this.formBuilder.group({
      integrator_address: ['', Validators.required],
      integrator_city: ['', [Validators.required, alphabeticValidatorInt()]],
      integrator_state: ['', Validators.required],
      integrator_zip: ['', [Validators.required, zipCodeValidatorInt()]],
      integrator_country: [{ value: 'US', disabled: true }],
      integrator_f_name: ['', Validators.required],
      integrator_l_name: ['', Validators.required],
      integrator_company: ['', Validators.required],
      integrator_email: [
        '',
        [Validators.required, strictEmailValidatorIntegrator],
      ],
      integrator_phone: ['', [cleanPhoneNumberValidatorInt()]],
    });
    this.billingDetailsGroupInt = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      address: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
      country: ['US'],
    });
    this.wholesalerGroup = this.formBuilder.group({
      wholesalerUserEmail: ['', Validators.required],
      referencePO: [''],
    });
  }
  /* istanbul ignore next */
  get metafieldsGroup(): FormGroup {
    return this.secondFormGroup.get('metafields') as FormGroup;
  }
  /* istanbul ignore next */
  createComponentControl(): FormGroup {
    return this.formBuilder.group({
      component: ['', Validators.required],
      allocatedQuantity: [
        1,
        [Validators.required, Validators.min(1), integerValidatorIntegrator()],
      ],
      numberOfUnits: [null],
      numberOfPerimeterAmenity: [null],
    });
  }
  /* istanbul ignore next */
  get components(): FormArray {
    return this.secondFormGroup.get('components') as FormArray;
  }
  /* istanbul ignore next */
  addComponent(): void {
    this.components.push(this.createComponentControl());
  }
  /* istanbul ignore next */
  removeComponent(index: number): void {
    if (this.components.length > 1) {
      this.components.removeAt(index);
      this.updateSelectedComponentsInt();
    } else {
      console.warn('At least one component is required.');
    }
  }
  /* istanbul ignore next */
  onPropertyUnknownToggle(event: MatCheckboxChange): void {
    // Read the checkbox state
    this.propertyAddressUnknown = !!event.checked;

    const metafields = this.metafieldsGroup;
    const fields = [
      'property_address',
      'property_city',
      'property_state',
      'property_zip',
    ];

    if (this.propertyAddressUnknown) {
      // User marked address unknown -> remove validators and set 'unknown'
      fields.forEach((field) => {
        const control = metafields.get(field);
        if (!control) return;

        control.clearValidators();
        control.setValue('unknown', { emitEvent: false });
        // keep enabled so .value includes them; if you prefer disabled, use disable() and remember to use getRawValue()
        control.markAsPristine();
        control.markAsUntouched();
        control.updateValueAndValidity({ emitEvent: false });
      });

      // Clear suggestions and mark that no valid address is selected
      this.addressSuggestions = [];
      this.validAddressSelected = false;
    } else {
      // User unchecked -> re-add validators, enable, and clear 'unknown' placeholders
      const addressControl = metafields.get('property_address');
      const cityControl = metafields.get('property_city');
      const stateControl = metafields.get('property_state');
      const zipControl = metafields.get('property_zip');

      // Re-add validators
      addressControl?.setValidators([Validators.required]);
      cityControl?.setValidators([
        alphabeticValidatorInt(),
        Validators.required,
      ]);
      stateControl?.setValidators([Validators.required]);
      zipControl?.setValidators([zipCodeValidatorInt(), Validators.required]);

      // Enable controls (if they were disabled previously) and clear the placeholder values
      [addressControl, cityControl, stateControl, zipControl].forEach(
        (ctrl) => {
          if (!ctrl) return;
          ctrl.enable({ emitEvent: false });
          // Reset to empty so the user must provide real values
          ctrl.reset('', { emitEvent: false });
          ctrl.markAsPristine();
          ctrl.markAsUntouched();
          ctrl.updateValueAndValidity({ emitEvent: false });
        }
      );

      // Optionally clear address suggestion state
      this.addressSuggestions = [];
      this.validAddressSelected = false;
    }

    // Trigger change detection in a minimal way (useful if component uses OnPush).
    // If you don't use OnPush you can remove this line.
    this.cdr.markForCheck();
  }
  /* istanbul ignore next */
  copyPropertyDetailsToChargifyInt() {
    if (this.usePropertyDetails) {
      const propertyDetails = this.customerInfoGroupInt.value;
      this.billingDetailsGroupInt.setValue({
        firstName: propertyDetails.integrator_f_name,
        lastName: propertyDetails.integrator_l_name,
        address: propertyDetails.integrator_address,
        city: propertyDetails.integrator_city,
        state: propertyDetails.integrator_state,
        zip: propertyDetails.integrator_zip,
        country: propertyDetails.integrator_country,
      });
      this.billingDetailsGroupInt.disable(); // Disable inputs
    } else {
      this.billingDetailsGroupInt.reset({
        country: 'US', // Set default country value
      });
      this.billingDetailsGroupInt.enable(); // Enable inputs
    }
    this.chargifyFormComponent.clearPaymentError();
  }
  /* istanbul ignore next */
  onPaymentMethodChange(isAch: boolean) {
    this.isAch = isAch; // Update the parent's isAch flag
    console.log('Payment method changed. isAch:', this.isAch);
  }
  /* istanbul ignore next */
  private getStepCount(): number {
    return this.stepper?.steps?.length || 0;
  }
  /* istanbul ignore next */
  shouldShowNextButton(): boolean {
    return (
      (this.stepper?.selectedIndex ?? this.getStepCount()) <
      this.getStepCount() - 1
    );
  }
  /* istanbul ignore next */
  shouldShowSaveButton(): boolean {
    return (this.stepper?.selectedIndex ?? -1) === this.getStepCount() - 1;
  }
  /* istanbul ignore next */
  isAchSelected(): boolean {
    return this.chargifyFormComponent?.isAch || false;
  }
  /* istanbul ignore next */
  selectProductInt(product: ProductResponse): void {
    this.selectedProduct = product;
    const productId = product.product.id;
    this.isExistingZentraConnectSite = null;
    this.firstFormGroup.patchValue({
      maxioProductId: productId.toString(),
      product_id: productId.toString(),
      product_name: product.product.name,
      isExistingZentraConnectSite: null,
    });

    const siteCtrl = this.firstFormGroup.get('isExistingZentraConnectSite')!;
    if (this.productService.productHasExistingSiteVariants(productId)) {
      siteCtrl.setValidators(Validators.required);
    } else {
      siteCtrl.clearValidators();
    }
    siteCtrl.updateValueAndValidity({ emitEvent: false });

    const poCtrl = this.firstFormGroup.get('poNumber')!;
    poCtrl.clearValidators();
    poCtrl.setValue('', { emitEvent: false });
    poCtrl.updateValueAndValidity({ emitEvent: false });

    this.applyComponentMapping(productId);
    this.cdr.markForCheck();
  }

  /* istanbul ignore next */
  onExistingZentraConnectSiteChange(value: 'YES' | 'NO'): void {
    this.isExistingZentraConnectSite = value;
    this.firstFormGroup.patchValue({ isExistingZentraConnectSite: value });

    const poCtrl = this.firstFormGroup.get('poNumber')!;
    if (value === 'YES') {
      poCtrl.setValidators(Validators.required);
    } else {
      poCtrl.clearValidators();
      poCtrl.setValue('', { emitEvent: false });
    }
    poCtrl.updateValueAndValidity({ emitEvent: false });

    if (this.selectedProduct) {
      this.applyComponentMapping(this.selectedProduct.product.id);
      this.cdr.markForCheck();
    }
  }

  /* istanbul ignore next */
  private applyComponentMapping(productId: number): void {
    const entries = this.authService.getComponentEntries(productId);
    const hasSiteVariants = entries.some(
      (e) => e.isExistingZentraConnectSite !== null
    );

    let selected:
      | {
          isExistingZentraConnectSite: ZentraConnectSiteStatus;
          optionalIds: number[];
          mandatoryIds: number[];
          qtyEditableIds: number[];
        }
      | undefined;
    if (entries.length === 1) {
      selected = entries[0];
    } else if (entries.length > 1 && this.isExistingZentraConnectSite) {
      selected = entries.find(
        (e) =>
          e.isExistingZentraConnectSite === this.isExistingZentraConnectSite
      );
    }

    if (!selected) {
      const awaitingSiteAnswer =
        hasSiteVariants && !this.isExistingZentraConnectSite;
      this.componentOptions = awaitingSiteAnswer
        ? []
        : [...this.allComponentOptions];
      this.mandatoryComponents = [];
      this.mandatoryQtyEditableIds = new Set();
    } else {
      this.componentOptions = selected.optionalIds.length
        ? this.allComponentOptions.filter((c) =>
            selected.optionalIds.includes(Number(c.id))
          )
        : [...this.allComponentOptions];
      this.mandatoryComponents = selected.mandatoryIds.length
        ? this.allComponentOptions.filter((c) =>
            selected.mandatoryIds.includes(Number(c.id))
          )
        : [];
      this.mandatoryQtyEditableIds = new Set(selected.qtyEditableIds ?? []);
    }

    this.preFillMandatoryComponents();
  }

  /* istanbul ignore next */
  private preFillMandatoryComponents(): void {
    while (this.components.length > 0) this.components.removeAt(0);
    this.mandatoryComponents.forEach((comp, index) => {
      const ctrl = this.createComponentControl();
      ctrl.patchValue({ component: comp, allocatedQuantity: 1 });
      ctrl.get('component')!.disable();
      if (!this.isMandatoryQtyEditable(index)) {
        ctrl.get('allocatedQuantity')!.disable();
      }
      this.connectComponentService.applyConnectFieldValidators(ctrl);
      this.components.push(ctrl);
    });
    this.addComponent();
  }

  /* istanbul ignore next */
  get selectedProductHasSiteVariants(): boolean {
    if (!this.selectedProduct) return false;
    return this.productService.productHasExistingSiteVariants(
      this.selectedProduct.product.id
    );
  }

  /* istanbul ignore next */
  isComponentMandatory(index: number): boolean {
    return index < this.mandatoryComponents.length;
  }

  /* istanbul ignore next */
  isMandatoryQtyEditable(index: number): boolean {
    const comp = this.mandatoryComponents[index];
    if (!comp) return false;
    return this.mandatoryQtyEditableIds.has(Number(comp.id));
  }

  /* istanbul ignore next */
  updateSelectedComponentsInt(): void {
    this.selectedComponents = this.components.controls
      .map((control) => {
        const component = control.get('component')?.value;
        const quantity = control.get('allocatedQuantity')?.value ?? 1;
        return component ? { component, quantity } : null;
      })
      .filter((item) => item !== null) as {
      component: ZentraComponent;
      quantity: number;
    }[];
    this.calculateTotalPriceInt();
  }
  /* istanbul ignore next */
  calculateTotalPriceInt(): void {
    this.totalPrice = calculateComponentsTotal(
      this.selectedComponents,
      this.components.controls
    );
  }
  /* istanbul ignore next */
  onComponentChange(component: ZentraComponent | null, index: number): void {
    if (!component) {
      console.warn('Component not found:', component);
      return;
    }
    // Check if the selected component is already chosen in another control
    const isDuplicate = this.components.controls.some((control, i) => {
      return (
        i !== index && control.get('component')?.value?.id === component.id
      );
    });
    if (isDuplicate) {
      alert(
        'This component is already selected. Please choose a different one.'
      );
      this.components.at(index).get('component')?.reset();
      return;
    }
    this.components.at(index).patchValue({
      component: component,
      allocatedQuantity:
        this.components.at(index).get('allocatedQuantity')?.value ?? 1,
    });
    this.connectComponentService.applyConnectFieldValidators(
      this.components.at(index) as FormGroup
    );
    this.updateSelectedComponentsInt();
  }
  /* istanbul ignore next */
  isComponentSelected(
    component: ZentraComponent,
    currentIndex: number
  ): boolean {
    return this.components.controls.some((control, index) => {
      return (
        index !== currentIndex &&
        control.get('component')?.value?.id === component.id
      );
    });
  }

  /* istanbul ignore next */
  get isOneTimeProduct(): boolean {
    const id = this.selectedProduct?.product?.id;
    return this.productService.isOneTimeProductId(id);
  }

  /* istanbul ignore next */
  displayComponentName(component: ZentraComponent | null): string {
    return component ? component.name : '';
  }
  /* istanbul ignore next */
  onAddressInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const address = inputElement.value;
    this.validAddressSelected = false;
    if (address.length > 2) {
      this.fetchAddressSuggestions(address);
    }
  }
  /* istanbul ignore next */
  fetchAddressSuggestions(address: string): void {
    this.addressService.fetchFilteredAddressSuggestions(address).subscribe({
      next: (filteredSuggestions) => {
        this.addressSuggestions = filteredSuggestions;
      },
      error: (error) => {
        console.error('Failed to fetch address suggestions:', error);
      },
    });
  }
  /* istanbul ignore next */
  selectSuggestionInt(suggestion: any): void {
    if (
      this.addressService.isValidStreetAddress(
        suggestion.address.freeformAddress
      )
    ) {
      this.updateFormWithAddressInt(suggestion.address);
      this.addressSuggestions = []; // Clear suggestions after selection
      this.validAddressSelected = true;
    } else {
      console.error(
        'Invalid address selected:',
        suggestion.address.freeformAddress
      );
    }
  }
  /* istanbul ignore next */
  selectSuggestion(suggestion: any): void {
    if (
      this.addressService.isValidStreetAddress(
        suggestion.address.freeformAddress
      )
    ) {
      this.updateFormWithAddress(suggestion.address);
      this.addressSuggestions = [];
      this.validAddressSelected = true;
    } else {
      console.error(
        'Invalid address selected:',
        suggestion.address.freeformAddress
      );
    }
  }
  /* istanbul ignore next */
  enterManualAddress(): void {
    this.addressSuggestions = [];
    this.validAddressSelected = true;
  }
  /* istanbul ignore next */
  validateChargifyForm(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.chargifyFormComponent) {
        console.error('ChargifyFormComponent not initialized');
        resolve(false);
        return;
      }
      this.subscriptions.add(
        this.chargifyFormComponent.validationSuccess.subscribe(() => {
          this.chargifyFormValid = true;
          this.chargifyToken = this.chargifyFormComponent.token;
          resolve(true);
        })
      );
      this.subscriptions.add(
        this.chargifyFormComponent.validationFailure.subscribe(() => {
          this.chargifyFormValid = false;
          resolve(false);
        })
      );
      this.chargifyFormComponent.validateChargifyForm();
    });
  }
  /* istanbul ignore next */
  onChargifyValidationSuccess() {
    console.log('Chargify form validation successful.');
    this.chargifyFormValid = true;
    this.chargifyToken = this.chargifyFormComponent.token; // Store the token
  }
  /* istanbul ignore next */
  onChargifyValidationFailure() {
    console.error('Chargify form validation failed.');
    this.chargifyFormValid = false;
  }
  /* istanbul ignore next */
  onAchTermsAcceptedChange(status: boolean) {
    this.achTermsAccepted = status;
    console.log('ACH Terms Accepted:', this.achTermsAccepted);
  }
  /* istanbul ignore next */
  onAchAgreementComplete(agreement: any) {
    this.achAgreement = agreement;
    console.log('ACH Agreement Data:', this.achAgreement);
  }
  /* istanbul ignore next */
  updateFormWithAddressInt(address: any): void {
    this.customerInfoGroupInt.patchValue({
      integrator_address: `${address.streetNumber} ${address.streetName}`, // Street address
      integrator_city: address.municipality, // City
      integrator_state: address.countrySubdivisionCode, // State (abbreviation)
      integrator_zip: address.postalCode, // ZIP code
      integrator_country: address.countryCode, // Country code
    });
  }
  /* istanbul ignore next */
  updateFormWithAddress(address: any): void {
    this.metafieldsGroup.patchValue({
      property_address: `${address.streetNumber} ${address.streetName}`, // Street address
      property_city: address.municipality, // City
      property_state: address.countrySubdivisionCode, // State (abbreviation)
      property_zip: address.postalCode, // ZIP code
      property_country: address.countryCode, // Country code
    });
  }
  /* istanbul ignore next */
  goToStep(stepIndex: number) {
    if (this.stepper) {
      this.stepper.selectedIndex = stepIndex; // Navigate to the specified step
    }
  }
  /* istanbul ignore next */
  async goToNextStep() {
    const currentStep = this.stepper.selectedIndex;
    if (currentStep === 3) {
      // Step 3: trigger Chargify async validation/token generation
      const isChargifyValid = await this.validateChargifyForm();
      if (isChargifyValid && this.chargifyFormComponent.token) {
        this.chargifyToken = this.chargifyFormComponent.token;
        await this.previewSubscription();
        if (this.maxioErrors.length > 0) {
          console.error('Maxio errors present, cannot proceed.');
          return;
        }
        this.stepper.next();
      } else {
        console.error('Chargify validation or token generation failed.');
        // Optionally show error message here
      }
    } else if (currentStep === 2) {
      // Step 2: Customer Information - handle disabled form for existing customer
      if (this.hasExistingCustomer || this.customerInfoGroupInt?.valid) {
        this.stepper.next();
      } else {
        this.customerInfoGroupInt.markAllAsTouched();
      }
    } else {
      // For other steps, normal validation and next
      const formGroups = [
        this.firstFormGroup,
        this.secondFormGroup,
        this.customerInfoGroupInt,
        this.paymentGroup,
      ];
      formGroups[currentStep].markAllAsTouched();
      if (formGroups[currentStep]?.valid) {
        this.stepper.next();
      }
    }
  }
  /* istanbul ignore next */
  goToPreviousStep() {
    if (this.stepper) {
      this.stepper.previous();
    }
  }
  /* istanbul ignore next */
  isCurrentStepValid() {
    const currentStep = this.stepper.selectedIndex;

    // Step 2: Customer Information
    if (currentStep === 2) {
      // If we have existing customer, the form is disabled but should be valid
      if (this.hasExistingCustomer) {
        return true;
      }
      // Otherwise, check form validity
      return this.customerInfoGroupInt?.valid ?? false;
    }

    // Step 3: Payment & Revenue Share
    if (currentStep === 3) {
      // 1. Check ACH Terms (Existing logic)
      const achValid = this.isAchSelected() ? this.achTermsAccepted : true;

      // If user is GP, the wholesalerGroup MUST be valid.
      // If user is Integrator, we ignore it (return true).
      const wholesalerValid =
        this.userRole === 'GuestPurchaser' ? this.wholesalerGroup.valid : true;

      return achValid && wholesalerValid;
    }

    // Step 4: Summary & Purchase
    if (currentStep === 4) {
      if (!this.termsAccepted) return false;
      const achValid = this.isAchSelected() ? this.achTermsAccepted : true;
      return achValid;
    }

    // Steps 0, 1
    const formGroups = [
      this.firstFormGroup,
      this.secondFormGroup,
      this.customerInfoGroupInt,
      this.paymentGroup,
    ];
    return formGroups[currentStep]?.valid ?? false;
  }

  /* istanbul ignore next */
  async previewSubscription(): Promise<void> {
    this.loadingService.show();
    try {
      console.log('Purchasing subscription with payload:');
      const payload = this.createPreviewPayload();
      console.log('Payload for purchase:', payload);
      // Use firstValueFrom instead of deprecated toPromise()
      const response = await firstValueFrom(
        this.backend.previewSubscription(payload)
      );
      console.log('Preview response:', response);
      if (response?.maxioErrors?.length > 0) {
        console.error('Maxio Errors:', response.maxioErrors);
        this.maxioErrors = response.maxioErrors; // Store the errors
        this.loadingService.hide();
        return; // Prevent proceeding to the next step
      }
      this.previewData = response;
      const manifest = response?.subscriptionPreview?.currentBillingManifest;
      this.billingDate = manifest?.endDate;
      this.lineItems = manifest?.lineItems ?? [];
      this.subtotal = manifest?.subtotalInCents / 100 || 0;
      this.totalTax = manifest?.totalTaxInCents / 100 || 0;
      this.total = manifest?.totalInCents / 100 || 0;
      this.maxioErrors = [];
      this.loadingService.hide();
      // Advance the stepper only here if you want to automatically move next after preview
      this.stepper.next();
    } catch (error) {
      console.error('Preview error:', error);
      this.loadingService.hide();
      // Optionally show an error message to the user here
    }
  }
  /* istanbul ignore next */
  createPreviewPayload(): any {
    const customerInfo = this.customerInfoGroupInt.value;
    const firstForm = this.firstFormGroup.value;
    const secondForm = this.secondFormGroup.value;
    const metafieldsData = this.metafieldsGroup.value;

    // Check if there's an existing customer ID (from your store/service)
    const existingCustomerId = this.maxioCustomerService.currentCustomerId;

    // Build customer_attributes only if no existing customer ID
    const customerAttributes = existingCustomerId
      ? null
      : {
          first_name: customerInfo.integrator_f_name ?? null,
          last_name: customerInfo.integrator_l_name ?? null,
          organization: customerInfo.integrator_company ?? null,
          email: customerInfo.integrator_email ?? null,
          address: customerInfo.integrator_address ?? null,
          city: customerInfo.integrator_city ?? null,
          state: customerInfo.integrator_state ?? null,
          zip: customerInfo.integrator_zip ?? null,
          country: customerInfo.integrator_country ?? null,
          phone: customerInfo.integrator_phone ?? null,
        };

    const subscriptionDetails: any = {
      product_handle: firstForm.product_handle ?? null,
      product_id: firstForm.product_id ?? null,
      product_price_point_id: firstForm.product_price_point_id ?? null,
      next_billing_at: secondForm.next_billing_at ?? null,
      coupon_code: null, // Add if applicable
      customer_id: existingCustomerId ?? null, // Use existing if available
      customer_attributes: customerAttributes, // Only send if no existing customer
      credit_card_attributes: this.isAch
        ? null
        : { chargify_token: this.chargifyToken, payment_type: 'CreditCard' },
      bank_account_attributes: this.isAch
        ? { chargify_token: this.chargifyToken, payment_type: 'BankAccount' }
        : null,
      ach_agreement: this.isAch
        ? {
            agreement_terms: this.achAgreement?.agreement_terms ?? null,
            authorizer_first_name:
              this.achAgreement?.authorizer_first_name ?? null,
            authorizer_last_name:
              this.achAgreement?.authorizer_last_name ?? null,
            ip_address: this.achAgreement?.ip_address ?? null,
          }
        : null,
      components: this.selectedComponents.map((comp) => ({
        component_id: comp.component.id.toString(),
        allocated_quantity: comp.quantity.toString(),
        price_point_id:
          comp.component.prices?.[0]?.pricePointId?.toString() || null,
      })),
      metafields: {
        property_name: metafieldsData.property_name ?? null,
        property_manager_email: metafieldsData.property_manager_email ?? null,
        property_manager_phone: metafieldsData.property_manager_phone ?? null,
        property_manager_f_name: metafieldsData.property_manager_f_name,
        property_manager_l_name: metafieldsData.property_manager_l_name ?? null,
        projectType: metafieldsData.projectType ?? null,
        implementationStartDate: metafieldsData.implementationStartDate ?? null,
        expectedOccupancyDate: metafieldsData.expectedOccupancyDate ?? null,
        engageMigration: metafieldsData.engageMigration ?? null,
        engageSiteName: metafieldsData.engageSiteName ?? null,
        engageSiteId: metafieldsData.engageSiteId ?? null,
        expectedMigrationDate: metafieldsData.expectedMigrationDate ?? null,
        propertyWebsite: metafieldsData.propertyWebsite ?? null,
        property_address: metafieldsData.property_address ?? null,
        property_city: metafieldsData.property_city ?? null,
        property_state: metafieldsData.property_state ?? null,
        property_zip: metafieldsData.property_zip ?? null,
        property_country: metafieldsData.property_country ?? 'US',
        wholesaler_email:
          this.wholesalerGroup.get('wholesalerUserEmail')?.value ?? null,
        wholesaler_name:
          this.getSelectedWholesalerName() !== 'None Selected' &&
          this.getSelectedWholesalerName() !== 'N/A'
            ? this.getSelectedWholesalerName()
            : null,
        po_number: this.firstFormGroup.get('poNumber')?.value || null,
        reference_po: this.wholesalerGroup.get('referencePO')?.value || null,
        number_of_units:
          this.connectComponentService
            .getConnectComponentControl(this.components)
            ?.get('numberOfUnits')
            ?.value?.toString() ?? null,
        number_of_perimeter_amenity:
          this.connectComponentService
            .getConnectComponentControl(this.components)
            ?.get('numberOfPerimeterAmenity')
            ?.value?.toString() ?? null,
      },
    };

    return {
      subscriptionDetails,
    };
  }
  /* istanbul ignore next */
  get selectedWholesaler(): any {
    const selectedEmail = this.wholesalerGroup?.get(
      'wholesalerUserEmail'
    )?.value;
    if (!selectedEmail) return null;

    return (
      this.wholesalers.find((ws) => ws.userEmail === selectedEmail) ?? null
    );
  }
  /* istanbul ignore next */
  getSelectedWholesalerName(): string {
    const selectedEmail = this.wholesalerGroup.get(
      'wholesalerUserEmail'
    )?.value;
    if (!selectedEmail) return 'None Selected';

    const foundWholesaler = this.wholesalers.find(
      (ws) => ws.userEmail === selectedEmail
    );

    return foundWholesaler
      ? `${foundWholesaler.userFirstName} ${foundWholesaler.userLastName} - ${foundWholesaler.userCompany}`
      : 'N/A';
  }
  /* istanbul ignore next */
  async onPreviewInvoiceClick(): Promise<void> {
    this.loadingService.show();

    if (this.loadingInvoicePreview) {
      this.loadingService.hide(); // ensure loading is hidden before returning
      return;
    }

    // If already have URL cached, open it instantly
    if (this.invoicePreviewUrl) {
      this.previewInvoiceSvc.openInvoiceInNewTab(this.invoicePreviewUrl);
      this.loadingService.hide(); // hide loading before returning
      return;
    }

    const payload = this.createPreviewPayload();
    if (!payload) {
      this.invoicePreviewError = 'Unable to build invoice payload.';
      this.loadingService.hide(); // hide loading before returning
      return;
    }

    this.loadingInvoicePreview = true;
    this.invoicePreviewError = null;

    try {
      const publicUrl = await this.previewInvoiceSvc.getPublicUrl(payload);
      this.invoicePreviewUrl = publicUrl; // cache for later
      this.previewInvoiceSvc.openInvoiceInNewTab(publicUrl);
    } catch (err: any) {
      console.error('Invoice preview failed', err);
      this.invoicePreviewError =
        (err?.message as string) ??
        'Failed to generate invoice preview. Please try again.';
    } finally {
      this.loadingInvoicePreview = false;
      this.loadingService.hide(); // always hide loading at the end
    }
  }

  /* istanbul ignore next */
  async purchaseSubscription() {
    this.loadingService.show();

    // 1. Determine the role
    const role = await this.getRoleType();

    // 2. Create the base payload (This returns { subscriptionDetails: { ... } })
    const basePayload = this.createPreviewPayload();

    // 3. Prepare the final payload and choose the endpoint
    let finalPayload;
    let purchaseObs$;

    if (role === 'GuestPurchaser') {
      finalPayload = {
        wholesalerUserEmail: this.wholesalerGroup.get('wholesalerUserEmail')
          ?.value,
        roleCode: 'GPRC',
        subscriptionDetails: basePayload.subscriptionDetails,
      };

      console.log('Purchasing as GuestPurchaser. Payload:', finalPayload);
      purchaseObs$ = this.backend.createSubscriptionGP(finalPayload);
    } else {
      // Standard Integrator
      finalPayload = {
        wholesalerUserEmail: null,
        roleCode: 'ZINT',
        subscriptionDetails: basePayload.subscriptionDetails,
      };
      purchaseObs$ = this.backend.createSubscriptionIntegrator(finalPayload);
    }

    // 4. Execute the call
    purchaseObs$.subscribe({
      next: (response) => {
        console.log('Purchase response:', response);
        this.loadingService.hide();

        if (response.errorMessage || response.maxioSubId == null) {
          console.error('Subscription creation failed:', response.errorMessage);
          alert(
            'Failed to create subscription. Please check your details and try again.'
          );
          return;
        }

        this.dialog.open(PurchaseConfirmationDialogComponent, {
          width: '500px',
          disableClose: true,
          data: {
            productName: this.selectedProduct?.product?.name ?? 'N/A',
            subscriptionId: response.maxioSubId,
          },
        });
      },
      error: (error) => {
        console.error('Purchase error:', error);
        this.loadingService.hide();
        alert('Failed to create subscription. Please try again.');
      },
    });
  }
}

/* istanbul ignore next */
export function cleanPhoneNumberValidatorInt(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;
    if (!rawValue) {
      return null; // No value, no error
    }
    // Remove all non-numeric characters (e.g., spaces, hyphens, parentheses, plus signs, etc.)
    const cleanedValue = rawValue.replace(/\D/g, '');
    // Check if the cleaned value is exactly 10 digits
    const isValid = /^\d{10}$/.test(cleanedValue);
    if (!isValid) {
      return {
        invalidPhoneNumber: {
          requiredLength: 10,
          actualLength: cleanedValue.length,
        },
      };
    }
    return null; // Valid phone number
  };
}
/* istanbul ignore next */
export function todayOrFutureDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // no value, no error
    }
    const inputDate = new Date(value);
    const today = new Date();
    // Reset time to midnight for accurate comparison
    inputDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    // Return error if inputDate is before today
    return inputDate < today ? { pastDate: true } : null;
  };
}
/* istanbul ignore next */
export function zipCodeValidatorInt(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // allow empty (optional)
    }
    // Define regex patterns for US and Canadian zip codes
    const usZipPattern = /^\d{5}$/; // Matches 5 digits or ZIP+4 format
    // const canadaZipPattern = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/; // Matches Canadian zip format
    // Check if the value matches either pattern
    const isValid = usZipPattern.test(value);
    // Return validation result
    return isValid ? null : { invalidZipCode: true };
  };
}
/* istanbul ignore next */
export function alphabeticValidatorInt(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // allow empty (optional)
    }
    // Allow letters, spaces, and hyphens (optional)
    const isValid = /^[a-zA-Z\s-]+$/.test(value);
    return isValid ? null : { nonAlphabetic: true };
  };
}
/* istanbul ignore next */
export function integerValidatorIntegrator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null; // Don't validate empty here, use required validator if needed
    }
    // Check if value is an integer (no decimals)
    const isInteger = Number.isInteger(Number(value));
    return !isInteger ? { notInteger: true } : null;
  };
}
/* istanbul ignore next */
export function strictEmailValidatorIntegrator(
  control: AbstractControl
): ValidationErrors | null {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const value = control.value;
  if (value && !emailRegex.test(value)) {
    return { invalidEmail: true };
  }
  return null;
}
